import React, { useState, useEffect, useMemo } from "react";
import CustomChart from "../Charts";
import styles from "./kpiCard.module.scss";

const KPILineChart = ({
  data,
  theme,
  smooth = false,
  hideLegend = false,
  xLabelRotation = 0,
}) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const tickColor = theme === "dark" ? "white" : "black";
  const rotation = Number(xLabelRotation) || 0;

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: tickColor,
            maxRotation: rotation,
            minRotation: rotation,
            autoSkip: true,
          },
        },
        y: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            color: tickColor,
          },
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: !hideLegend,
          position: "bottom",
          labels: {
            color: tickColor,
            usePointStyle: true,
            padding: 20,
          },
        },
      },
      backgroundColor: "transparent",
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: rotation > 0 ? 28 : 10,
        },
      },
    }),
    [tickColor, hideLegend, rotation],
  );

  useEffect(() => {
    try {
      if (!data || !data.datasets || !data.labels) {
        setError("No data found");
        return;
      }

      // Process the data to ensure it matches Chart.js format.
      // null points = missing period (no reviews / no rating) → gap; dashed across gaps.
      const processedData = {
        labels: data.labels,
        datasets: data.datasets.map((dataset) => {
          const hasNulls = Array.isArray(dataset.data)
            ? dataset.data.some((v) => v == null)
            : false;
          const wantDashedGaps =
            hasNulls ||
            dataset.gapStyle === "dashed" ||
            dataset.spanGaps === true;
          return {
            ...dataset,
            fill: smooth ? "origin" : dataset.fill != null ? dataset.fill : false,
            tension: smooth ? 0.42 : 0.4,
            ...(smooth ? { cubicInterpolationMode: "monotone" } : {}),
            pointRadius: (ctx) => {
              const y = ctx?.parsed?.y;
              if (y == null || Number.isNaN(y)) return 0;
              return smooth ? 0 : 4;
            },
            pointHoverRadius: smooth ? 5 : 6,
            borderWidth: smooth ? 2.5 : 2,
            spanGaps: wantDashedGaps ? true : Boolean(dataset.spanGaps),
            ...(wantDashedGaps
              ? {
                  segment: {
                    borderDash: (ctx) => {
                      const y0 = ctx?.p0?.parsed?.y;
                      const y1 = ctx?.p1?.parsed?.y;
                      const skipped =
                        ctx?.p0?.skip ||
                        ctx?.p1?.skip ||
                        y0 == null ||
                        y1 == null ||
                        Number.isNaN(y0) ||
                        Number.isNaN(y1);
                      return skipped ? [6, 6] : undefined;
                    },
                  },
                }
              : {}),
          };
        }),
      };

      setChartData(processedData);
      setError(null);
    } catch (err) {
      console.error("Error processing chart data:", err);
      setError("Error processing chart data");
    }
  }, [data, smooth]);

  if (error) {
    return <div className={styles.errorMessage}>{error}</div>;
  }

  if (!chartData) {
    return <div className={styles.loadingMessage}>Loading chart data...</div>;
  }

  return (
    <div className={styles.chartContainer}>
      <CustomChart type="line" data={chartData} options={options} theme={theme}/>
    </div>
  );
};

export default KPILineChart;
